import React, {useState} from 'react';
import {Autocomplete, TextField, Box, Button, FormControl, InputLabel, Select, MenuItem, Paper} from '@mui/material';
import {
    endOfMonth,
    endOfWeek,
    isBefore,
    isValid,
    isWithinInterval,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
    subWeeks
} from 'date-fns';

function Filters({initialList, setFilteredList}: any) {
    const data = initialList;
    const [analysisID, setAnalysisID] = useState(null);
    const [title, setTitle] = useState(null);
    const [status, setStatus] = useState('');
    const [selectedDateRange, setSelectedDateRange] = useState('');

    const getStatusOptions = () => ['completed', 'failed', 'processing'];
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    const lastMonth = subMonths(now, 1);

    const dateRanges = [
        {label: 'This week', start: startOfWeek(now), end: endOfWeek(now)},
        {label: 'Last week', start: startOfWeek(lastWeek), end: endOfWeek(lastWeek)},
        // last two weeks is actually a 3 week time frame, start of week 2 weeks ago through end of current week
        {label: 'Last two weeks', start: startOfWeek(subWeeks(now, 2)), end: endOfWeek(now)},
        {label: 'Last month', start: startOfMonth(lastMonth), end: endOfMonth(lastMonth)},
        {label: '90+ days ago', start: subDays(now, 90), end: null}
    ];

    const filterData = () => {
        const range = dateRanges.find(range => range.label === selectedDateRange);

        const filtered = data.filter((item: any) => {
            const timeStarted = new Date(item.time_started);
            if (!isValid(timeStarted)) {
                return false;
            }

            const withinDateRange = range
                ? range.end
                    ? isWithinInterval(timeStarted, {start: range.start, end: range.end})
                    : isBefore(timeStarted, range.start)
                : true;
            const idMatches = analysisID ? item?.analysis_num === analysisID : true;
            const statusMatches = status ? item?.status === status : true;
            const titleMatches = title ? item?.title === title : true;

            return withinDateRange && idMatches && statusMatches && titleMatches;
        });
        setFilteredList(filtered);
    };

    const resetFilters = () => {
        setAnalysisID(null);
        setTitle(null);
        setStatus('');
        setSelectedDateRange('');
        setFilteredList(null);
    };

    return (
        <Paper elevation={3} sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 1, width: 350}}>
            <Autocomplete
                options={data?.map((option: {analysis_num: any}) => option.analysis_num)}
                value={analysisID}
                onChange={(_event, newValue) => {
                    setAnalysisID(newValue);
                }}
                renderInput={params => <TextField {...params} label="Analysis ID" />}
            />
            <Autocomplete
                options={data?.map((option: {title: any}) => option.title)}
                value={title}
                onChange={(_event, newValue) => {
                    setTitle(newValue);
                }}
                renderInput={params => <TextField {...params} label="Analysis Name" />}
            />
            <FormControl fullWidth>
                <InputLabel id="date-range-label">Date Range</InputLabel>
                <Select
                    labelId="date-range-label"
                    id="date-range-select"
                    value={selectedDateRange}
                    label="Date Range"
                    onChange={e => setSelectedDateRange(e.target.value)}>
                    {dateRanges.map((option, index) => (
                        <MenuItem key={index} value={option.label}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                    labelId="status-select-label"
                    id="status-select"
                    value={status}
                    label="Status"
                    onChange={e => setStatus(e.target.value)}>
                    {getStatusOptions().map((option, index) => (
                        <MenuItem key={index} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box sx={{mt: 2, display: 'flex', justifyContent: 'space-between'}}>
                <Button variant="outlined" onClick={resetFilters}>
                    Reset
                </Button>
                <Button variant="contained" onClick={filterData}>
                    View results
                </Button>
            </Box>
        </Paper>
    );
}

export default Filters;
